(function () {
  'use strict';

  /**
   * section-petition-signatures.js
   * ────────────────────────────────
   * Drives the animated horizontal signatures bar.
   *
   * On load
   *   → bar animates from 0 → current fill percentage (entrance transition)
   *   → remaining count is formatted with locale thousands separator
   *
   * On each new signature
   *   → bar width increases by (1 / goal) * 100%
   *   → remaining count decrements by 1
   *   → bar fill pulses briefly to draw attention to the change
   *
   * ─── Public API ─────────────────────────────────────────────────
   *
   *   window.PetitionSignatures.increment(sectionId?)
   *     Increments all bars on the page, or just the one whose
   *     data-section-id matches the optional sectionId argument.
   *
   * ─── Custom DOM event ───────────────────────────────────────────
   *
   *   document.dispatchEvent(new CustomEvent('petition:signed'))
   *     Increments all bars. Dispatch this from your form handler.
   *
   * ─── Example usage ──────────────────────────────────────────────
   *
   *   // Option A — custom event (preferred, decoupled)
   *   document.dispatchEvent(new CustomEvent('petition:signed'));
   *
   *   // Option B — direct call
   *   window.PetitionSignatures.increment();
   */

  var instances = [];

  /* ─── Bar instance ──────────────────────────────────────────── */

  function SignaturesBar(el) {
    this.el          = el;
    this.fill        = el.querySelector('.petition-signatures__fill');
    this.track       = el.querySelector('.petition-signatures__track');
    this.remainingEl = el.querySelector('.petition-signatures__remaining');
    this.count       = parseInt(el.dataset.currentCount || '0', 10);
    this.goal        = parseInt(el.dataset.goalCount    || '1',  10); // the max amount the progress bar can show
    this.trueGoal    = 10000; // the actual goal for signatures
    this.fillPercent = parseFloat(el.dataset.fillPercent || '0');
    this.remaining   = Math.max(this.trueGoal - this.count, 0);
    this.remainingHeading = el.querySelector('.petition-signatures__card-heading');
    this.remainingPreheading = el.querySelector('.petition-signatures__card-eyebrow');

    if (this.goal <= 0) this.goal = 1;

    this._init();
  }

  SignaturesBar.prototype._init = async function () {
    var self = this;

    // Set the post submission code - increases count (both server side and client)
    this.klaviyoSubmissionHook(self);

    // this.count = 3000;

    // Fetch count from our server
    this.count = await this.fetchCount();

    // If we received a count from the server use that count as 
    if (this.count) {
      // Get the current fill percentage
      const currentFillPercent = Math.min(parseFloat((this.count / this.goal) * 100), 100);
      this.el.dataset.fillPercent = currentFillPercent;

      // Set remaining and fillPercentage so we can correctly display how many signatures left and the current progress
      this.fillPercent = currentFillPercent;

      // If our call to the rest endpoint was successful, update the remaining with our count from the server
      this.remaining = Math.max(this.trueGoal - this.count, 0);
    }

    /* Format the initial remaining count with thousands separator */
    this._renderRemaining();

    /* Format milestone numbers with thousands separator (e.g. 1776 → 1,776) */
    this.el.querySelectorAll('.petition-signatures__milestone-num').forEach(function (el) {
      var num = parseInt(el.textContent, 10);
      if (!isNaN(num)) el.textContent = num.toLocaleString();
    });

    /* Entrance animation:
       Force a starting width of 0%, then set the target on the
       next two animation frames so the browser paints zero first
       and the CSS transition fires from 0 → target. */
    if (this.fill) {
      this.fill.style.width = '0%';

      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          self.fill.style.width = self.fillPercent.toFixed(2) + '%';
        });
      });
    }
  };

  // Internal updateCount method - this function code is already called in assets/klaviyo-success.js since there are 2 forms on 2 separate pages that push to the same list/segment
  // In order to keep the count true, the code within the function does not need to be fired from within this class
  SignaturesBar.prototype.updateCount = async function() {
    const { token } = await fetch('https://letitryed.com/wp-json/petition/v1/token')
      .then(r => r.json());
    
    const response = await fetch('https://letitryed.com/wp-json/petition/v1/count', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Petition-JWT': token
      },
    });
  }

  SignaturesBar.prototype.fetchCount = async function () {
    const { count } = await fetch('https://letitryed.com/wp-json/petition/v1/count')
    .then(r => r.json()).catch(error => {
        throw new Error('Unable to fetch count', error);
    });

    return count;
  }

  SignaturesBar.prototype.klaviyoSubmissionHook = function (self) {
    window.addEventListener("klaviyoForms", function(e) {
      if (e.detail.type == 'submit') {
        // Peitition form on both petition/sweeps page
        if (e.detail.formId === 'UfHwUk') {
          // self.updateCount();
          self.increment();
        }
      }
    });
  }

  SignaturesBar.prototype._renderRemaining = function () {
    if (this.remainingEl) {
      this.remainingEl.textContent   = this.remaining.toLocaleString();
      this.remainingEl.dataset.remaining = this.remaining;
    }

    if (this.remaining === 0) {
      this.remainingPreheading.textContent = `We've Reached Our`;
      this.remainingHeading.textContent = 'SIGNATURES GOAL'
    }

    if (this.track) {
      this.track.setAttribute('aria-valuenow', this.count);
    }
  };

  SignaturesBar.prototype.increment = function () {
    var self = this;

    this.count      += 1;
    this.remaining   = Math.max(this.trueGoal - this.count, 0);
    this.fillPercent = Math.min((this.count / this.goal) * 100, 100);

    /* Animate the bar width — CSS transition handles the easing */
    if (this.fill) {
      this.fill.style.width = this.fillPercent.toFixed(2) + '%';

      /* Pulse */
      this.fill.classList.remove('is-pulsing');
      requestAnimationFrame(function () {
        self.fill.classList.add('is-pulsing');
      });
      this.fill.addEventListener('animationend', function onEnd() {
        self.fill.classList.remove('is-pulsing');
        self.fill.removeEventListener('animationend', onEnd);
      });
    }

    this._renderRemaining();
  };

  /* ─── Initialise all bars on the page ───────────────────────── */

  function init() {
    document.querySelectorAll('.petition-signatures[data-section-id]').forEach(function (el) {
      instances.push(new SignaturesBar(el));
    });

    /* Listen for the petition:signed custom event.
       Dispatch from your Klaviyo / form-submit handler:
         document.dispatchEvent(new CustomEvent('petition:signed')); */
    document.addEventListener('petition:signed', function () {
      instances.forEach(function (bar) { bar.increment(); });
    });
  }

  /* ─── Public API ─────────────────────────────────────────────── */

  window.PetitionSignatures = {
    /**
     * increment(sectionId?)
     * Increment a specific bar by sectionId, or all bars if omitted.
     */
    increment: function (sectionId) {
      instances.forEach(function (bar) {
        if (!sectionId || bar.el.dataset.sectionId === String(sectionId)) {
          bar.increment();
        }
      });
    }
  };

  /* ─── Boot ───────────────────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
